import React, { useState, useContext, useRef, useEffect } from 'react';
import styled from 'styled-components';

import { validateReadableId } from '@builttocreate/engine-utils';
import ButtonGroupSettings from './ButtonGroupSettings';
import InputSettingsField from './InputSettingsField';
import CheckboxSettingsField from './CheckboxSettingsField';
import FieldStyleSettings from './FieldStyleSettings';
import PositionSettings from './PositionSettings';
import OptionSettings from './OptionSettings';
import MetadataSettings from './MetadataSettings';
import DecoratorSettings from './DecoratorSettings';
import IdentifierSelectorSettings from './IdentifierSelectorSettings';
import FieldSettingsHeaderTitle from './FieldSettingsHeaderTitle';
import Button from '../../misc/Button';
import Icon from '../../misc/Icon';
import ConditionalLogicSettings from './ConditionalLogicSettings';
import RequiredLogicSettings from './RequiredLogicSettings';
import SettingsTabs from './SettingsTabs';
import TooltipSettings from './TooltipSettings';
import AccordionSettings from './AccordionSettings';
import SettingsLabel from './SettingsLabel';
import TitleSettings from './TitleSettings';
import { StyledFieldWrapper, StyledAccordionFieldWrapper, StyledButtonGroupWrapper, StyledButtonsWrapper, DividerLine } from './SettingsCommonElements';

import ThemeIcons from '../../../theme/Icons';
import ThemeContext from '../../../theme/ThemeContext';

import FieldDisplayTypes from '../../../constants/FieldDisplayTypes';
import FieldTargetDisplayTypes from '../../../constants/FieldTargetDisplayTypes';
import FileViews from '../../../constants/FileViews';
import FieldTypes from '../../../constants/FieldTypes';

import { isSettingHidden, isStyleSettingHidden, getMetadataOptions } from '../../../lib/settingsHelper';

const StyledWrapper = styled.div`
  padding: 12px 0px;
  display: flex;
  flex-direction: column;
`;

const StyledErrorMessage = styled.div`
  color: ${(props) => props.theme.dangerColor};
  font-size: 12px;
  margin-top: 4px;
  margin-left: 3px;
`;

const FieldSettingsMultiSelectField = ({

  //Logic properties
  fileId,
  sortedPages,
  fields,
  logic,
  requiredLogic,

  view,
  fieldId,
  fieldIdentifier,
  fieldPositionId,
  fieldSection,

  activeFieldPosition,
  title,
  tipTitle,
  tipDescription,
  tipVisible,
  type,
  displayType,
  columnType,
  options,
  invertBooleanCondition,
  multi,
  metadata,
  config,
  hidden,
  hiddenViews,
  disabled,
  value,

  targetValue,
  targetValueDisplayType,
  customTrueTargetDisplayValue,
  customFalseTargetDisplayValue,

  //API Developers
  identifier,
  required,

  //Position
  x,
  y,
  width,
  height,

  //Title styles
  titleDisplay,
  titleFontSize,
  titleFontColor,
  titleTextAlign,
  titleFontStyle,
  titleFontWeight,
  titleTextTransform,
  titleTextDecoration,

  //Field style
  fontSize,
  fontColor,
  fontStyle,
  fontWeight,
  textAlign,
  textTransform,
  textDecoration,
  textOverflow,
  borderColor,
  borderRadius,
  borderWidth,
  backgroundColor,
  padding,
  margin,
  zIndex,

  //Action handlers
  onChange,
  onDelete,
  onDuplicate,
  onPositionDuplicate,
  onOptionCreate,
  onOptionChange,
  onOptionDelete,
  useReadableIds,

  decorators,
  onDecoratorCreate,
  onDecoratorChange,
  onDecoratorDelete,
}) => {
  const theme = useContext(ThemeContext);
  const [readableIdError, setReadableIdError] = useState('');
  const prevProps = useRef();

  useEffect(() => {

    if (prevProps.current && prevProps.current !== fieldId && readableIdError) {
      setReadableIdError('');
    }

    prevProps.current = fieldId;

  }, [fieldId, setReadableIdError]);

  const isStylesHidden = isSettingHidden(config, 'styles');
  const isDisplayTypeHidden = isSettingHidden(config, 'displayType');
  const isTitleHidden = isSettingHidden(config, 'title');
  const isValueHidden = isSettingHidden(config, 'value');
  const isDisabledHidden = isSettingHidden(config, 'disabled');
  const isRequiredHidden = isSettingHidden(config, 'required');
  const isHiddenHidden = isSettingHidden(config, 'hidden');
  const isVisibilityHidden = isSettingHidden(config, 'hiddenViews');
  const isDecoratorsHidden = isSettingHidden(config, 'decorators');
  const isMultiHidden = isSettingHidden(config, 'multi');
  const isOptionsHidden = isSettingHidden(config, 'options');
  const isLogicHidden = isSettingHidden(config, 'logic');
  const isTooltipHidden = isSettingHidden(config, 'tooltip');
  const isIdentifierHidden = isSettingHidden(config, 'identifier');
  const isDeleteHidden = isSettingHidden(config, 'delete');
  const isInvertBooleanConditionHidden = isSettingHidden(config, 'invertBooleanCondition');
  const isTargetValueDisplayTypeHidden = isSettingHidden(config, 'targetValueDisplayType');
  const isIdHidden = isSettingHidden(config, '_id');

  //Title Styles
  const isTitleFontSizeHidden = isStyleSettingHidden(config, 'titleFontSize');
  const isTitleFontColorHidden = isStyleSettingHidden(config, 'titleFontColor');
  const isTitleTextAlignHidden = isStyleSettingHidden(config, 'titleTextAlign');
  const isTitleFontStyleHidden = isStyleSettingHidden(config, 'titleFontStyle');
  const isTitleFontWeightHidden = isStyleSettingHidden(config, 'titleFontWeight');
  const isTitleTextTransformHidden = isStyleSettingHidden(config, 'titleTextTransform');
  const isTitleTextDecorationHidden = isStyleSettingHidden(config, 'titleTextDecoration');

  //Text Styles
  const isFontSizeHidden = isStyleSettingHidden(config, 'fontSize');
  const isFontColorHidden = isStyleSettingHidden(config, 'fontColor');
  const isTextAlignHidden = isStyleSettingHidden(config, 'textAlign');
  const isFontStyleHidden = isStyleSettingHidden(config, 'fontStyle');
  const isFontWeightHidden = isStyleSettingHidden(config, 'fontWeight');
  const isTextTransformHidden = isStyleSettingHidden(config, 'textTransform');
  const isTextDecorationHidden = isStyleSettingHidden(config, 'textDecoration');
  const isTextOverflowHidden = isStyleSettingHidden(config, 'textOverflow');

  //Field Styles
  const isBorderColorHidden = isStyleSettingHidden(config, 'borderColor');
  const isBorderRadiusHidden = isStyleSettingHidden(config, 'borderRadius');
  const isBorderWidthHidden = isStyleSettingHidden(config, 'borderWidth');
  const isBackgroundColorHidden = isStyleSettingHidden(config, 'backgroundColor');
  const isMarginHidden = isStyleSettingHidden(config, 'margin');
  const isPaddingHidden = isStyleSettingHidden(config, 'padding');
  const isZIndexHidden = isStyleSettingHidden(config, 'zIndex');
  const isPositionHidden = isStyleSettingHidden(config, 'position');

  const isHeaderOrFooter = fieldSection === 'header' || fieldSection === 'footer';

  const metadataOptions = getMetadataOptions(config);

  let nextFieldSettingsIcon = null;
  if (type === FieldTypes.dropdown) nextFieldSettingsIcon = ThemeIcons.fields.dropdown;
  if (type === FieldTypes.multiSelect) nextFieldSettingsIcon = multi ? ThemeIcons.fields.multiSelect : ThemeIcons.fields.multiSelectSingle;

  const isDropdownType = type === FieldTypes.dropdown;
  const isSelectorType = displayType && (displayType !== FieldDisplayTypes.original && displayType !== FieldDisplayTypes.horizontal);

  const validOptions = [];
  if (options) {
    options.forEach((option) => {
      if (!option.deleted) validOptions.push(option);
    });
  }

  const stylesNode = (
    <>
      {(isTitleFontSizeHidden
        && isTitleFontColorHidden
        && isTitleTextAlignHidden
        && isTitleFontStyleHidden
        && isTitleFontWeightHidden
        && isTitleTextTransformHidden
        && isTitleTextDecorationHidden) ? null : (
        <>
          <AccordionSettings
            isOpen
            title="Title"
            height={170}
          >
            <FieldStyleSettings
              id={`${fieldId}-title`}
              {...{
                fontSize: titleFontSize,
                onFontSizeChange: isTitleFontSizeHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleFontSize: value }),

                fontColor: titleFontColor,
                onFontColorChange: isTitleFontColorHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleFontColor: value }),

                textAlign: titleTextAlign,
                onTextAlignChange: isTitleTextAlignHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleTextAlign: value }),

                fontStyle: titleFontStyle,
                onFontStyleChange: isTitleFontStyleHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleFontStyle: value }),

                fontWeight: titleFontWeight,
                onFontWeightChange: isTitleFontWeightHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleFontWeight: value }),

                textTransform: titleTextTransform,
                onTextTransformChange: isTitleTextTransformHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleTextTransform: value }),

                textDecoration: titleTextDecoration,
                onTextDecorationChange: isTitleTextDecorationHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleTextDecoration: value }),
              }}
            />
          </AccordionSettings>
          <DividerLine />
        </>
      )}
      {(
        isFontSizeHidden
        && isFontColorHidden
        && isTextAlignHidden
        && isFontStyleHidden
        && isFontWeightHidden
        && isTextTransformHidden
        && isTextDecorationHidden
        && isTextOverflowHidden
      ) ? null : (
        <>
          <AccordionSettings
            isOpen
            title="Text"
            height={170}
          >
            <FieldStyleSettings
              id={`${fieldId}-field`}
              {...{

                fontSize,
                onFontSizeChange: isFontSizeHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { fontSize: value }),

                fontColor,
                onFontColorChange: isFontColorHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { fontColor: value }),

                textAlign,
                onTextAlignChange: isTextAlignHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { textAlign: value }),

                fontStyle,
                onFontStyleChange: isFontStyleHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { fontStyle: value }),

                fontWeight,
                onFontWeightChange: isFontWeightHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { fontWeight: value }),

                textTransform,
                onTextTransformChange: isTextTransformHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { textTransform: value }),

                textDecoration,
                onTextDecorationChange: isTextDecorationHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { textDecoration: value }),

                textOverflow,
                onTextOverflowChange: isTextOverflowHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { textOverflow: value }),

              }}
            />
          </AccordionSettings>
          <DividerLine />
        </>
      )}
      {(
        isBorderColorHidden
        && isBorderRadiusHidden
        && isBorderWidthHidden
        && isBackgroundColorHidden
        && isMarginHidden
        && isPaddingHidden
        && isPositionHidden
      ) ? null : (
        <AccordionSettings
          isOpen
          title="Field"
          height={400}
        >
          <FieldStyleSettings
            id={`${fieldId}-field`}
            {...{

              borderColor,
              onBorderColorChange: isBorderColorHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { borderColor: value }),

              borderRadius,
              onBorderRadiusChange: isBorderRadiusHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { borderRadius: value }),

              borderWidth,
              onBorderWidthChange: isBorderWidthHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { borderWidth: value }),

              backgroundColor,
              onBackgroundColorChange: isBackgroundColorHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { backgroundColor: value }),

              padding,
              onPaddingChange: isPaddingHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { padding: value }),

              margin,
              onMarginChange: isMarginHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { margin: value }),

              zIndex,
              onZIndexChange: isZIndexHidden ? null : (value) => (value ? onChange(fieldId, fieldIdentifier, fieldPositionId, { zIndex: value }) : ''),

            }}
          />
          {isPositionHidden ? null : (
            <PositionSettings
              {...{
                view,
                x,
                y,
                width,
                height,
                onChange: (field, value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { [field]: value })
              }}
            />
          )}
        </AccordionSettings>
      )}
    </>
  );

  const readableIdNode = isIdHidden || !useReadableIds ? null : (
    <StyledFieldWrapper>
      <InputSettingsField
        label="ID"
        fieldId={fieldId}
        value={fieldId}
        type="text"
        rows={3}
        error={readableIdError ? true : false}
        disabled={!useReadableIds}
        onBlur={(value) => {

          if (value && !validateReadableId(value, fields) && value !== fieldId) {

            setReadableIdError(`Invalid: Can't be duplicate or start with numerics, use reserved ES6 keywords, or have spaces.`);

          } else {

            setReadableIdError('');

            if (value && value !== fieldId) {
              onChange(fieldId, fieldIdentifier, fieldPositionId, { _id: value });
            }

          }
        }}
      />
      {readableIdError && (
        <StyledErrorMessage theme={theme}>
          {readableIdError}
        </StyledErrorMessage>
      )}
    </StyledFieldWrapper>
  );


  const settingsNode = (
    <>
      {isDisplayTypeHidden || view === FileViews.mobile ? null : (
        <StyledButtonGroupWrapper>
          <ButtonGroupSettings
            id="displayType"
            types={[
              { type: FieldDisplayTypes.original, icon: ThemeIcons.settings.selectDisplayTypeOriginal, active: displayType === FieldDisplayTypes.original },
              isDropdownType ? null : { type: FieldDisplayTypes.horizontal, icon: ThemeIcons.settings.selectDisplayTypeHorizontal, active: displayType === FieldDisplayTypes.horizontal },
              isDropdownType ? null : { type: FieldDisplayTypes.text, icon: ThemeIcons.settings.selectDisplayTypeText, active: displayType === FieldDisplayTypes.text },
              { type: FieldDisplayTypes.check, icon: ThemeIcons.settings.selectDisplayTypeCheck, active: displayType === FieldDisplayTypes.check },
              { type: FieldDisplayTypes.radio, icon: ThemeIcons.settings.selectDisplayTypeRadio, active: displayType === FieldDisplayTypes.radio },
              { type: FieldDisplayTypes.circle, icon: ThemeIcons.settings.selectDisplayTypeCircle, active: displayType === FieldDisplayTypes.circle },
              { type: FieldDisplayTypes.square, icon: ThemeIcons.settings.selectDisplayTypeSquare, active: displayType === FieldDisplayTypes.square },
            ]}
            onChange={(value) => {
              onChange(fieldId, fieldIdentifier, fieldPositionId, { displayType: value });
            }}
          />
        </StyledButtonGroupWrapper>
      )}
      {isTitleHidden ? null : (
        <>
          <SettingsLabel>Title</SettingsLabel>
          <DividerLine style={{ marginBottom: 14 }} />
          <TitleSettings
            {...{
              title,
              titleDisplay
            }}
            onChange={(changes) => onChange(fieldId, fieldIdentifier, fieldPositionId, changes)}
          />
        </>
      )}
      {isOptionsHidden ? null : (
        <>
          {!isSelectorType ? null : (
            <StyledFieldWrapper>
              <InputSettingsField
                type="dropdown"
                fieldId={fieldPositionId}
                addonBefore={<Icon icon={ThemeIcons.settings.targetValue} />}
                value={targetValue}
                options={validOptions}
                onChange={(i, value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { targetValue: value })}
              />
            </StyledFieldWrapper>
          )}
          <StyledFieldWrapper>
            <OptionSettings
              id="options"
              config={config?.options}
              activeFieldPosition={activeFieldPosition}
              type={type}
              displayType={displayType}
              options={options}
              onChange={(optionId, data) => onOptionChange(fieldId, fieldIdentifier, fieldPositionId, optionId, data)}
              onDelete={(optionId) => onOptionDelete(fieldId, fieldIdentifier, fieldPositionId, optionId)}
              onCreate={(data) => onOptionCreate(fieldId, fieldIdentifier, fieldPositionId, data)}
              targetValue={targetValue}
              columnType={columnType}
            />
          </StyledFieldWrapper>
        </>
      )}
      {(isOptionsHidden
        && isMultiHidden
        && isRequiredHidden
        && isDisabledHidden
      ) ? null : (
        <>
          <SettingsLabel>Field</SettingsLabel>
          <DividerLine style={{ marginBottom: 14 }} />
        </>
      )}
      {isValueHidden || isSelectorType || !isDropdownType ? null : (
        <StyledFieldWrapper>
          <InputSettingsField
            type="dropdown"
            fieldId={fieldId}
            label="Default Value"
            value={value}
            options={validOptions}
            onChange={(i, value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { value })}
          />
        </StyledFieldWrapper>
      )}
      {isMultiHidden || isDropdownType ? null : (
        <StyledFieldWrapper>
          <CheckboxSettingsField
            id="multi"
            icon={ThemeIcons.settings.multi}
            iconColor={multi ? theme.primaryColor : theme.borderColor}
            label="Multiple Choice"
            selected={multi}
            onChange={(selected) => onChange(fieldId, fieldIdentifier, fieldPositionId, { multi: selected },)}
          />
        </StyledFieldWrapper>
      )}
      {isDisabledHidden ? null : (
        <StyledFieldWrapper>
          <CheckboxSettingsField
            icon={ThemeIcons.settings.disabled}
            iconColor={disabled ? theme.primaryColor : theme.borderColor}
            id="disabled"
            label="Read-only"
            selected={disabled}
            onChange={(selected) => onChange(fieldId, fieldIdentifier, fieldPositionId, { disabled: selected },)}
          />
        </StyledFieldWrapper>
      )}
      {isRequiredHidden ? null : (
        !isHeaderOrFooter && (
          <StyledFieldWrapper>
            <CheckboxSettingsField
              icon={ThemeIcons.settings.required}
              iconColor={required ? theme.dangerColor : theme.borderColor}
              iconSize="1x"
              id="required"
              label="Required"
              selected={required}
              onChange={(selected) => onChange(fieldId, fieldIdentifier, fieldPositionId, { required: selected },)}
            />
          </StyledFieldWrapper>
        )
      )}
      {isHeaderOrFooter || fields.length < 2 ? null : (
        <>
          <SettingsLabel>Required Logic</SettingsLabel>
          <DividerLine style={{ marginBottom: 14 }} />
          <StyledFieldWrapper>
            <RequiredLogicSettings
              fileId={fileId}
              targetLabel="field"
              targetFieldId={fieldId}
              sortedPages={sortedPages}
              fields={fields}
              logic={requiredLogic}
              onSubmit={(nextLogic) => onChange(fieldId, fieldIdentifier, fieldPositionId, { requiredLogic: nextLogic })}
            />
          </StyledFieldWrapper>
        </>
      )}
      {(isHiddenHidden && (isLogicHidden || fields.length < 2)) ? null : (
        !isHeaderOrFooter && (
          <>
            <SettingsLabel>Conditional Logic</SettingsLabel>
            <DividerLine style={{ marginBottom: 14 }} />
            {isLogicHidden || fields.length < 2 ? null : (
              <StyledFieldWrapper>
                <ConditionalLogicSettings
                  fileId={fileId}
                  targetLabel="field"
                  targetFieldId={fieldId}
                  sortedPages={sortedPages}
                  fields={fields}
                  logic={logic}
                  onSubmit={(nextLogic) => onChange(fieldId, fieldIdentifier, fieldPositionId, { logic: nextLogic })}
                />
              </StyledFieldWrapper>
            )}
            {isHiddenHidden ? null : (
              <StyledFieldWrapper>
                <CheckboxSettingsField
                  icon={ThemeIcons.settings.hide}
                  iconColor={hidden ? theme.warningColor : theme.borderColor}
                  id="hidden"
                  label="Hidden"
                  selected={hidden}
                  onChange={(selected) => onChange(fieldId, fieldIdentifier, fieldPositionId, { hidden: selected },)}
                />
              </StyledFieldWrapper>
            )}
          </>
        )
      )}
      {isVisibilityHidden ? null : (
        !isHeaderOrFooter && (
          <>
            <SettingsLabel>Visibility</SettingsLabel>
            <DividerLine style={{ marginBottom: 14 }} />
            <StyledFieldWrapper>
              <CheckboxSettingsField
                icon={ThemeIcons.settings.hide}
                iconColor={hiddenViews?.includes('desktop') ? theme.warningColor : theme.borderColor}
                id="hiddenViewsDesktop"
                label="Hide on desktop"
                selected={hiddenViews?.includes('desktop') || false}
                onChange={(selected) => {
                  const next = selected
                    ? [...(hiddenViews || []), 'desktop']
                    : (hiddenViews || []).filter(v => v !== 'desktop');
                  onChange(fieldId, fieldIdentifier, fieldPositionId, { hiddenViews: next });
                }}
              />
            </StyledFieldWrapper>
            <StyledFieldWrapper>
              <CheckboxSettingsField
                icon={ThemeIcons.settings.hide}
                iconColor={hiddenViews?.includes('mobile') ? theme.warningColor : theme.borderColor}
                id="hiddenViewsMobile"
                label="Hide on mobile"
                selected={hiddenViews?.includes('mobile') || false}
                onChange={(selected) => {
                  const next = selected
                    ? [...(hiddenViews || []), 'mobile']
                    : (hiddenViews || []).filter(v => v !== 'mobile');
                  onChange(fieldId, fieldIdentifier, fieldPositionId, { hiddenViews: next });
                }}
              />
            </StyledFieldWrapper>
            <StyledFieldWrapper>
              <CheckboxSettingsField
                icon={ThemeIcons.settings.hide}
                iconColor={hiddenViews?.includes('pdf') ? theme.warningColor : theme.borderColor}
                id="hiddenViewsPdf"
                label="Hide on pdf"
                selected={hiddenViews?.includes('pdf') || false}
                onChange={(selected) => {
                  const next = selected
                    ? [...(hiddenViews || []), 'pdf']
                    : (hiddenViews || []).filter(v => v !== 'pdf');
                  onChange(fieldId, fieldIdentifier, fieldPositionId, { hiddenViews: next });
                }}
              />
            </StyledFieldWrapper>
          </>
        )
      )}
      {isDecoratorsHidden ? null : (
        <>
          <SettingsLabel>Decorators</SettingsLabel>
          <DividerLine style={{ marginBottom: 14 }} />
          <DecoratorSettings
            decorators={decorators}
            onCreate={(data) => onDecoratorCreate(fieldId, fieldIdentifier, fieldPositionId, data)}
            onChange={(decoratorAction, data) => onDecoratorChange(fieldId, fieldIdentifier, fieldPositionId, decoratorAction, data)}
            onDelete={(decoratorAction) => onDecoratorDelete(fieldId, fieldIdentifier, fieldPositionId, decoratorAction)}
          />
        </>
      )}
      {!metadataOptions ? null : (
        <>
          <DividerLine />
          <MetadataSettings
            metadata={metadata}
            metadataOptions={metadataOptions}
            onChange={(changes) => {
              const nextMetadata = metadata ? { ...metadata, ...changes } : { ...changes };
              onChange(fieldId, fieldIdentifier, fieldPositionId, { metadata: nextMetadata });
            }}
          />
        </>
      )}
      {(isInvertBooleanConditionHidden && isTargetValueDisplayTypeHidden) || isDropdownType || !isSelectorType ? null : (
        <>
          <DividerLine style={{ marginBottom: 0 }} />
          <AccordionSettings
            title="Advanced"
            height={targetValueDisplayType !== FieldTargetDisplayTypes.custom ? 80 : 208}
          >
            {isInvertBooleanConditionHidden ? null : (
              <StyledAccordionFieldWrapper>
                <CheckboxSettingsField
                  id="invertBooleanCondition"
                  label="Invert Boolean Condition"
                  selected={invertBooleanCondition}
                  onChange={(selected) => onChange(fieldId, fieldIdentifier, fieldPositionId, { invertBooleanCondition: selected })}
                />
              </StyledAccordionFieldWrapper>
            )}
            {isTargetValueDisplayTypeHidden || (displayType && displayType !== FieldDisplayTypes.text) ? null : (
              <>
                <StyledAccordionFieldWrapper>
                  <CheckboxSettingsField
                    id="targetValueDisplayType"
                    label="Use Custom True/False Text"
                    selected={targetValueDisplayType === FieldTargetDisplayTypes.custom}
                    onChange={(selected) => {
                      if (selected) {
                        onChange(fieldId, fieldIdentifier, fieldPositionId, { targetValueDisplayType: FieldTargetDisplayTypes.custom },);
                      } else {
                        onChange(fieldId, fieldIdentifier, fieldPositionId, { targetValueDisplayType: FieldTargetDisplayTypes.original },);
                      }
                    }}
                  />
                </StyledAccordionFieldWrapper>
                {targetValueDisplayType !== FieldTargetDisplayTypes.custom ? null : (
                  <>
                    <StyledAccordionFieldWrapper>
                      <InputSettingsField
                        inputStyles={{ maxWidth: '120px' }}
                        fieldId={fieldId}
                        label="True Text"
                        type="text"
                        value={customTrueTargetDisplayValue}
                        onChange={(i, value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { customTrueTargetDisplayValue: value })}
                      />
                    </StyledAccordionFieldWrapper>
                    <StyledAccordionFieldWrapper>
                      <InputSettingsField
                        inputStyles={{ maxWidth: '120px' }}
                        fieldId={fieldId}
                        label="False Text"
                        type="text"
                        value={customFalseTargetDisplayValue}
                        onChange={(i, value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { customFalseTargetDisplayValue: value })}
                      />
                    </StyledAccordionFieldWrapper>
                  </>
                )}
              </>
            )}
          </AccordionSettings>
        </>
      )}
      {readableIdNode ? (
        <>
          <DividerLine />
          {readableIdNode}
        </>
      ) : null}
      {isTooltipHidden ? null : (
        <>
          <DividerLine style={{ marginBottom: 0 }} />
          <AccordionSettings
            title="Tooltip"
            height={166}
          >
            <TooltipSettings
              {...{
                tipVisible,
                tipTitle,
                tipDescription
              }}
              onChange={(changes) => onChange(fieldId, fieldIdentifier, fieldPositionId, changes)}
            />
          </AccordionSettings>
        </>
      )}
      {isIdentifierHidden ? null : (
        <>
          <DividerLine style={{ marginBottom: 0 }} />
          <AccordionSettings
            title={config?.identifier?.label || 'Identifier'}
            height={60}
          >
            <IdentifierSelectorSettings
              fieldId={fieldId}
              fieldType={type}
              type="text"
              customValue={config?.identifier?.options}
              enableCustomOption={config?.identifier?.custom}
              value={identifier}
              onChange={(fieldId, value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { identifier: value })}
            />
          </AccordionSettings>
        </>
      )}
      {isDeleteHidden ? null : (
        <>
          <DividerLine style={{ marginBottom: 14 }} />
          <StyledButtonsWrapper>
            <Button
              danger
              icon={ThemeIcons.settings.delete}
              text="Delete"
              onClick={() => onDelete(fieldId, fieldIdentifier, fieldPositionId)}
            />
            <Button
              icon={ThemeIcons.settings.clone}
              text="Duplicate"
              onClick={() => {
                if (activeFieldPosition.displayType === 'original' || activeFieldPosition.displayType === 'inputGroup' || activeFieldPosition.displayType === 'horizontal') {
                  onDuplicate(fieldId, fieldPositionId);
                } else {
                  onPositionDuplicate(fieldId, fieldPositionId);
                }
              }}
              solid
            />
          </StyledButtonsWrapper>
        </>
      )}
    </>
  );

  return (
    <StyledWrapper data-testid="form-settings">
      <FieldSettingsHeaderTitle
        title="Settings"
        icon={nextFieldSettingsIcon}
      />
      <SettingsTabs
        settings={settingsNode}
        styles={isStylesHidden ? null : stylesNode}
      />
    </StyledWrapper>
  );

};

export default FieldSettingsMultiSelectField;
